import {
    Alert,
    AlertDescription,
    AlertIcon,
    AlertTitle,
    Box,
    IconButton,
    Skeleton,
    useDisclosure,
} from '@chakra-ui/react';
import ItemListWrapper from 'components/layout/ItemListWrapper';
import LayoutSidebar from 'components/layout/LayoutSidebar';
import LayoutWrapper from 'components/layout/LayoutWrapper';
import VirtualisedItemList from 'components/utils/VirtualisedItemList';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Outlet, useParams } from 'react-router-dom';
import { RootState } from 'store';
import { ReactComponent as StudyIcon } from '../icons/study.svg';
import { StudyFilterRequest, StudyResponse } from 'types/studies';
import { ReactComponent as FilterIcon } from '../icons/filter.svg';
import SideDrawer from 'components/utils/SideDrawer';
import SimpleGridSkeleton from 'components/utils/SimpleGridSkeleton';
import Error from 'components/utils/Error';
import StudyFilters from 'components/studies/StudyFilters';
import ItemLinkWrapper from 'components/layout/ItemLinkWrapper';
import { useGetStudies, useGetStudyFilterOptions } from 'query/studies';
import { getDefaultScheduledRunDateTimeFrom } from 'services/studies';
import useHistoryPush from 'hooks/useHistoryPush';

const includedStatuses = ['Completed'];

export const defaultStudyRequestParams: StudyFilterRequest = {
    scheduledRunDateTimeFrom: getDefaultScheduledRunDateTimeFrom().toDateString(),
    scheduledRunDateTimeTo: null,
    bookIds: [],
    statuses: includedStatuses,
    jobTypeIds: [],
    jobIds: [],
    studyIds: [],
    submittedByNames: [],
    searchNameQuery: '',
    clientOffset: 0,
    stdReportStudiesOnly: true,
};

export function ReportDashboard() {
    const intl = useIntl();
    const { id } = useParams();
    const historyPush = useHistoryPush();
    const filterBtnRef = useRef<HTMLButtonElement | null>(null);
    const { isSidebarVisible } = useSelector((state: RootState) => state.helpers);

    const filterDrawer = useDisclosure();
    const [getStudiesParams, setGetStudyParams] = useState<StudyFilterRequest>(defaultStudyRequestParams);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<StudyResponse | null>(null);

    const {
        data: getStudyResults,
        isFetching: isLoadingStudies,
        isError: loadingError,
    } = useGetStudies(getStudiesParams);

    const { data: filterOptions, isFetching: isFiltersLoading, isError: isFiltersError } = useGetStudyFilterOptions();
    const reportingFilterOptions = useMemo(() => {
        return {
            ...filterOptions,
            statuses: filterOptions?.statuses?.filter((status: string) => includedStatuses.includes(status)) || [],
        };
    }, [filterOptions]);

    const studies = useMemo(() => {
        return (
            getStudyResults?.data?.filter((study) => {
                const term = `[${study?.studyId}] ${study?.name}`;
                return term?.toLowerCase()?.includes(search.trim().toLowerCase());
            }) || []
        );
    }, [search, getStudyResults]);

    const itemIndex = useMemo(() => {
        return studies.findIndex((item: StudyResponse) => item.studyId === selected?.studyId);
    }, [studies, selected]);

    const noSearchResults = useMemo(() => {
        return search.trim().length > 0 && studies.length === 0;
    }, [search, studies]);

    const onResetFilters = () => {
        setSearch('');
        setGetStudyParams(defaultStudyRequestParams);
        filterDrawer.onClose();
    };

    const onApplyFilters = (updatedFilters: any) => {
    setGetStudyParams({
        ...updatedFilters,
        scheduledRunDateTimeFrom: updatedFilters?.scheduledRunDateTimeFrom 
            ? new Date(updatedFilters.scheduledRunDateTimeFrom).toDateString() 
            : null,
        scheduledRunDateTimeTo: updatedFilters?.scheduledRunDateTimeTo 
            ? new Date(updatedFilters.scheduledRunDateTimeTo).toDateString() 
            : null,
        stdReportStudiesOnly: true,
    });
    filterDrawer.onClose();
};

    useEffect(() => {
        if (isLoadingStudies) {
            return;
        }

        if (!id && studies?.length > 0) {
            historyPush(`/reports/${studies[0].studyId}`);
        }
    }, [id, studies, isLoadingStudies, historyPush]);

    return (
        <>
            <LayoutWrapper
                sidebar={
                    isSidebarVisible && (
                        <LayoutSidebar
                            itemType={null}
                            selected={selected}
                            titleId={`reporting-title`}
                            titleMessage={intl.formatMessage({ id: `reporting_sidebar_title` })}
                            accordionPlaceholderId={`reporting_select_item`}
                            searchPlaceholderId={`common_search_reports`}
                            searchCallback={(value: string) => setSearch(value)}
                            searchTerm={search}
                            extraButtons={
                                <IconButton
                                    ref={filterBtnRef}
                                    ml={2}
                                    aria-label={'expand'}
                                    size="xs"
                                    icon={<FilterIcon />}
                                    variant="circular-icon"
                                    onClick={filterDrawer.onOpen}
                                />
                            }
                        >
                            <ItemListWrapper>
                                <Box px={6} pb={2}>
                                    {isLoadingStudies ? (
                                        <Skeleton mt={4}>
                                            <Box h="34px">
                                                <FormattedMessage id="common_loading" />
                                            </Box>
                                        </Skeleton>
                                    ) : loadingError ? (
                                        <Alert mt={4} status="error">
                                            <AlertIcon />
                                            <Box>
                                                <AlertTitle textTransform="capitalize">
                                                    <FormattedMessage id="common_error" />
                                                </AlertTitle>

                                                <AlertDescription>
                                                    <FormattedMessage id="common_loading_error" />
                                                </AlertDescription>
                                            </Box>
                                        </Alert>
                                    ) : studies?.length === 0 ? (
                                        <Alert mt={4} status="info" textTransform="capitalize">
                                            <AlertIcon />
                                            <FormattedMessage id="common_no_results" />
                                        </Alert>
                                    ) : null}
                                </Box>

                                {!noSearchResults && !loadingError && !isLoadingStudies && (
                                    <VirtualisedItemList
                                        items={studies || []}
                                        itemIndex={itemIndex}
                                        renderItems={(study: StudyResponse) => (
                                            <ItemLinkWrapper
                                                itemId={study.studyId}
                                                isMaster={false}
                                                onClick={() => setSelected(study)}
                                                icon={StudyIcon}
                                                label={`[${study.studyId}] ${study.name}`}
                                            />
                                        )}
                                    />
                                )}
                            </ItemListWrapper>
                        </LayoutSidebar>
                    )
                }
                content={<Outlet />}
            ></LayoutWrapper>
            {filterDrawer.isOpen && (
                <SideDrawer
                    variant={''}
                    isOpen
                    onClose={filterDrawer.onClose}
                    header={<FormattedMessage id="study_tracker_drawer_heading" />}
                    content={
                        <>
                            {isFiltersLoading || isFiltersError ? (
                                <Box>
                                    {isFiltersLoading && <SimpleGridSkeleton rows={6} cols={1} />}
                                    {isFiltersError && (
                                        <Box m={6}>
                                            <Error
                                                primaryId="common_error"
                                                secondaryId="common_loading_error"
                                                additionalText={intl.formatMessage({
                                                    id: 'study_tracker_study_filters',
                                                })}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            ) : (
                                <StudyFilters
                                    filterOptions={reportingFilterOptions}
                                    filters={getStudiesParams}
                                    onApplyFilters={onApplyFilters}
                                    onResetFilters={onResetFilters}
                                    includeBooks={false}
                                    includeStatus={false}
                                    includeJobType={false}
                                />
                            )}
                        </>
                    }
                    placement="left"
                    closeOnEsc={true}
                    closeOnOverlayClick={true}
                />
            )}
        </>
    );
}
